/**
 * EmailProviderManager — reusable multi-provider email configuration UI.
 * Used by both Super Admin (platform scope) and Organization (org scope).
 *
 * Props:
 *   api         — { list, create, update, remove, test, activate, deactivate }
 *   readOnly    — boolean; hide all write actions
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NexusIcon } from '../ui/NexusIcon';
import { Btn, Pill } from '../ui';
import { useToast } from '../feedback';

// ── Provider type metadata ────────────────────────────────────────────────────

const PROVIDER_TYPES = [
  {
    value: 'ses',
    label: 'AWS SES',
    desc: 'Recommended for high-volume production transactional email.',
    icon: 'Cloud',
  },
  {
    value: 'aws_smtp',
    label: 'AWS SMTP',
    desc: 'Amazon SES via SMTP credentials — suitable for SMTP-compatible integrations.',
    icon: 'Server',
  },
  {
    value: 'smtp',
    label: 'Standard SMTP',
    desc: "Connect to your organization's SMTP server for transactional email delivery.",
    icon: 'Mail',
  },
];

const TYPE_LABEL = { ses: 'AWS SES', aws_smtp: 'AWS SMTP', smtp: 'SMTP' };

const EMPTY_SES    = { region: 'us-east-1', fromEmail: '', fromName: '', replyTo: '' };
const EMPTY_SMTP   = { host: '', port: '587', encryption: 'tls', fromEmail: '', fromName: '' };
const EMPTY_SECRET_SES  = { accessKeyId: '', secretAccessKey: '' };
const EMPTY_SECRET_SMTP = { username: '', password: '' };

function emptyConfig(type) {
  return type === 'ses' ? { ...EMPTY_SES } : { ...EMPTY_SMTP };
}
function emptySecret(type) {
  return type === 'ses' ? { ...EMPTY_SECRET_SES } : { ...EMPTY_SECRET_SMTP };
}

// ── Small UI helpers ──────────────────────────────────────────────────────────

const Field = ({ label, hint, children, required }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>
      {label}{required && <span style={{ color: 'var(--nx-danger)', marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{hint}</span>}
  </div>
);

const Input = ({ value, onChange, type = 'text', placeholder, disabled }) => (
  <input
    className="nx-input"
    type={type}
    value={value ?? ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
  />
);

const Select = ({ value, onChange, options, disabled }) => (
  <select className="nx-input" value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// ── Provider type badge ───────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  const colors = {
    ses:      { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
    aws_smtp: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    smtp:     { bg: '#faf5ff', color: '#7c3aed', border: '#ddd6fe' },
  };
  const c = colors[type] ?? colors.smtp;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {TYPE_LABEL[type] ?? type.toUpperCase()}
    </span>
  );
};

// ── Status dot ────────────────────────────────────────────────────────────────

const StatusDot = ({ active }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: active ? 'var(--nx-success, #16a34a)' : 'var(--nx-border)',
    flexShrink: 0,
  }} />
);

// ── Last tested display ───────────────────────────────────────────────────────

function formatRelative(date) {
  if (!date) return null;
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString();
}

// ── Provider card ─────────────────────────────────────────────────────────────

function ProviderCard({ provider, onEdit, onDelete, onTest, onActivate, onDeactivate, testingId, readOnly }) {
  const tested = formatRelative(provider.lastTestedAt);
  const isActive = provider.active;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 16px', borderRadius: 8,
      border: `1px solid ${isActive ? 'var(--nx-primary, #125AB0)' : 'var(--nx-border)'}`,
      background: isActive ? 'var(--nx-primary-tint, #eff6ff)' : 'var(--nx-surface)',
    }}>
      <StatusDot active={isActive} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--nx-text)' }}>{provider.name}</span>
          <TypeBadge type={provider.type} />
          {isActive && <Pill tone="green">Active</Pill>}
        </div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {provider.config.fromEmail && (
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              From: {provider.config.fromName
                ? `${provider.config.fromName} <${provider.config.fromEmail}>`
                : provider.config.fromEmail}
            </span>
          )}
          {provider.config.host && (
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              {provider.config.host}:{provider.config.port || 587}
            </span>
          )}
          {provider.config.region && provider.type === 'ses' && (
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Region: {provider.config.region}</span>
          )}
        </div>
        {tested && (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon
              name={provider.lastTestOk ? 'CheckCircle' : 'XCircle'}
              size={12}
              style={{ color: provider.lastTestOk ? 'var(--nx-success, #16a34a)' : 'var(--nx-danger)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              Last tested {tested} — {provider.lastTestOk ? 'passed' : 'failed'}
            </span>
          </div>
        )}
      </div>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {isActive ? (
            <Btn variant="ghost" size="sm" onClick={() => onDeactivate(provider.id)}>
              Deactivate
            </Btn>
          ) : (
            <Btn variant="secondary" size="sm" onClick={() => onActivate(provider.id)}>
              Activate
            </Btn>
          )}
          <Btn
            variant="ghost"
            size="sm"
            icon="Send"
            onClick={() => onTest(provider.id)}
            disabled={testingId === provider.id}
            title="Send a test email"
          >
            {testingId === provider.id ? 'Testing…' : 'Test'}
          </Btn>
          <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => onEdit(provider)} title="Edit" />
          <Btn variant="ghost" size="sm" icon="Trash2" onClick={() => onDelete(provider)} title="Delete"
            style={{ color: 'var(--nx-danger)' }} />
        </div>
      )}
    </div>
  );
}

// ── Provider form (inside modal) ──────────────────────────────────────────────

function ProviderForm({ form, onChange, isEdit }) {
  const { type, name, config, secret } = form;

  const setField  = (key, val) => onChange({ ...form, config: { ...config, [key]: val } });
  const setSecret = (key, val) => onChange({ ...form, secret: { ...secret, [key]: val } });
  const setType   = (newType)  => onChange({
    ...form, type: newType,
    config: emptyConfig(newType),
    secret: isEdit ? secret : emptySecret(newType),
  });

  const secretHint     = isEdit ? 'Leave blank to keep existing credentials.' : undefined;
  const smtpConfigTitle = type === 'aws_smtp' ? 'AWS SMTP Configuration' : 'SMTP Configuration';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Provider Name" required>
        <Input value={name} onChange={v => onChange({ ...form, name: v })} placeholder="e.g. Production SES" />
      </Field>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', marginBottom: 8, float: 'left', width: '100%' }}>
          Provider Type
        </legend>
        <div style={{ clear: 'both', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PROVIDER_TYPES.map(pt => (
            <label key={pt.value} aria-label={pt.label} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
              borderRadius: 6, cursor: 'pointer',
              border: `1px solid ${type === pt.value ? 'var(--nx-primary, #125AB0)' : 'var(--nx-border)'}`,
              background: type === pt.value ? 'var(--nx-primary-tint, #eff6ff)' : 'var(--nx-surface)',
            }}>
              <input
                type="radio"
                name="providerType"
                value={pt.value}
                checked={type === pt.value}
                onChange={() => setType(pt.value)}
                style={{ marginTop: 2 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--nx-text)' }}>{pt.label}</span>
                <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{pt.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {type === 'ses' && (
        <>
          <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              AWS SES Configuration
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="From Email" required>
                <Input value={config.fromEmail} onChange={v => setField('fromEmail', v)} placeholder="noreply@yourdomain.com" type="email" />
              </Field>
              <Field label="From Name">
                <Input value={config.fromName} onChange={v => setField('fromName', v)} placeholder="URSA" />
              </Field>
              <Field label="AWS Region" required>
                <Select value={config.region} onChange={v => setField('region', v)} options={[
                  { value: 'us-east-1',      label: 'us-east-1 (N. Virginia)' },
                  { value: 'us-west-2',      label: 'us-west-2 (Oregon)' },
                  { value: 'eu-west-1',      label: 'eu-west-1 (Ireland)' },
                  { value: 'eu-central-1',   label: 'eu-central-1 (Frankfurt)' },
                  { value: 'ap-south-1',     label: 'ap-south-1 (Mumbai)' },
                  { value: 'ap-southeast-1', label: 'ap-southeast-1 (Singapore)' },
                  { value: 'ap-northeast-1', label: 'ap-northeast-1 (Tokyo)' },
                ]} />
              </Field>
              <Field label="Reply-To">
                <Input value={config.replyTo} onChange={v => setField('replyTo', v)} placeholder="replies@yourdomain.com" type="email" />
              </Field>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              IAM Credentials
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Access Key ID" required={!isEdit} hint={secretHint}>
                <Input value={secret.accessKeyId} onChange={v => setSecret('accessKeyId', v)} placeholder={isEdit ? '••••••••••••••••' : 'AKIA...'} />
              </Field>
              <Field label="Secret Access Key" required={!isEdit} hint={secretHint}>
                <Input value={secret.secretAccessKey} onChange={v => setSecret('secretAccessKey', v)} type="password" placeholder={isEdit ? '••••••••' : 'Secret key'} />
              </Field>
            </div>
          </div>
        </>
      )}

      {(type === 'aws_smtp' || type === 'smtp') && (
        <>
          <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {smtpConfigTitle}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="SMTP Host" required>
                <Input value={config.host} onChange={v => setField('host', v)}
                  placeholder={type === 'aws_smtp' ? 'email-smtp.us-east-1.amazonaws.com' : 'smtp.yourdomain.com'} />
              </Field>
              <Field label="Port">
                <Select value={String(config.port)} onChange={v => setField('port', v)} options={[
                  { value: '25',  label: '25  (SMTP)'  },
                  { value: '465', label: '465 (SMTPS)' },
                  { value: '587', label: '587 (TLS)'   },
                ]} />
              </Field>
              <Field label="Encryption">
                <Select value={config.encryption} onChange={v => setField('encryption', v)} options={[
                  { value: 'none', label: 'None' },
                  { value: 'tls',  label: 'STARTTLS' },
                  { value: 'ssl',  label: 'SSL/TLS'  },
                ]} />
              </Field>
              <div />
              <Field label="From Email" required>
                <Input value={config.fromEmail} onChange={v => setField('fromEmail', v)} placeholder="noreply@yourdomain.com" type="email" />
              </Field>
              <Field label="From Name">
                <Input value={config.fromName} onChange={v => setField('fromName', v)} placeholder="URSA" />
              </Field>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Credentials
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Username" hint={secretHint}>
                <Input value={secret.username} onChange={v => setSecret('username', v)} placeholder={isEdit ? '(unchanged)' : 'smtp-user'} />
              </Field>
              <Field label="Password" hint={secretHint}>
                <Input value={secret.password} onChange={v => setSecret('password', v)} type="password" placeholder={isEdit ? '••••••••' : 'Password'} />
              </Field>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function DeleteConfirm({ provider, onConfirm, onCancel, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
    }}>
      <div style={{
        background: 'var(--nx-surface)', borderRadius: 10, padding: 28,
        width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <NexusIcon name="AlertTriangle" size={20} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--nx-text)' }}>Delete provider?</span>
        </div>
        <p style={{ fontSize: 14, color: 'var(--nx-text-muted)', margin: '0 0 24px' }}>
          <strong>{provider.name}</strong> will be permanently removed.
          {provider.active && ' This provider is currently active — email delivery will fall back to environment defaults.'}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="secondary" onClick={onCancel} disabled={deleting}>Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────

function Modal({ title, children, onClose, footer }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
    >
      <dialog
        ref={ref}
        open
        style={{ border: 'none', padding: 0, background: 'var(--nx-surface)', borderRadius: 10, width: 600, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--nx-text)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="X" size={18} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {children}
        </div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 24px', borderTop: '1px solid var(--nx-border)' }}>
            {footer}
          </div>
        )}
      </dialog>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EmailProviderManager({ api, readOnly = false }) {
  const toast = useToast();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | { mode: 'add'|'edit', form, saving }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [testingId, setTestingId] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    api.list()
      .then(data => setProviders(Array.isArray(data) ? data : []))
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // ── Add ──────────────────────────────────────────────────────────────────

  const openAdd = useCallback(() => {
    setModal({
      mode: 'add',
      form: { type: 'smtp', name: '', config: emptyConfig('smtp'), secret: emptySecret('smtp') },
      saving: false,
    });
  }, []);

  // ── Edit ─────────────────────────────────────────────────────────────────

  const openEdit = useCallback((provider) => {
    setModal({
      mode: 'edit',
      id: provider.id,
      form: {
        type: provider.type,
        name: provider.name,
        config: { ...emptyConfig(provider.type), ...provider.config },
        secret: emptySecret(provider.type),
      },
      saving: false,
    });
  }, []);

  // ── Save (create or update) ───────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!modal) return;
    const { form, mode, id } = modal;

    if (!form.name?.trim()) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Name required' });
      return;
    }

    setModal(m => ({ ...m, saving: true }));
    try {
      const hasSecretValues = Object.values(form.secret || {}).some(Boolean);
      const body = {
        type: form.type,
        name: form.name.trim(),
        config: form.config,
        ...(hasSecretValues ? { secret: form.secret } : {}),
      };

      if (mode === 'add') {
        await api.create(body);
        toast({ tone: 'success', icon: 'Check', title: 'Provider added' });
      } else {
        await api.update(id, body);
        toast({ tone: 'success', icon: 'Check', title: 'Provider updated' });
      }
      setModal(null);
      reload();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: e?.message });
      setModal(m => ({ ...m, saving: false }));
    }
  }, [modal, api, toast, reload]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.remove(deleteTarget.id);
      toast({ tone: 'success', icon: 'Check', title: 'Provider removed' });
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: e?.message });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, api, toast, reload]);

  // ── Test ──────────────────────────────────────────────────────────────────

  const handleTest = useCallback(async (id) => {
    setTestingId(id);
    try {
      const result = await api.test(id);
      if (result.success) {
        toast({ tone: 'success', icon: 'CheckCircle', title: 'Connection successful', desc: result.info });
      } else {
        toast({ tone: 'error', icon: 'AlertCircle', title: 'Connection failed', desc: result.error });
      }
      reload();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Test failed', desc: e?.message });
    } finally {
      setTestingId(null);
    }
  }, [api, toast, reload]);

  // ── Activate ──────────────────────────────────────────────────────────────

  const handleActivate = useCallback(async (id) => {
    try {
      await api.activate(id);
      toast({ tone: 'success', icon: 'Check', title: 'Provider activated', desc: 'Transactional emails will now use this provider.' });
      reload();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Activation failed', desc: e?.message });
    }
  }, [api, toast, reload]);

  // ── Deactivate ────────────────────────────────────────────────────────────

  const handleDeactivate = useCallback(async (id) => {
    try {
      await api.deactivate(id);
      toast({ tone: 'success', icon: 'Check', title: 'Provider deactivated', desc: 'No active email provider is set.' });
      reload();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Deactivation failed', desc: e?.message });
    }
  }, [api, toast, reload]);

  // ── Render helpers ────────────────────────────────────────────────────────

  const hasActive = providers.some(p => p.active);
  const plural    = providers.length !== 1 ? 's' : '';
  const activeLabel = hasActive ? '1 active' : 'none active';
  const summaryText = providers.length === 0
    ? 'No providers configured. Only one provider can be active at a time.'
    : `${providers.length} provider${plural} — ${activeLabel}`;

  const renderList = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2].map(i => (
            <div key={i} className="nx-cc-skeleton" style={{ height: 72, borderRadius: 8 }} />
          ))}
        </div>
      );
    }
    if (providers.length === 0) {
      return (
        <div style={{
          padding: '32px 20px', textAlign: 'center', borderRadius: 8,
          border: '1px dashed var(--nx-border)', color: 'var(--nx-text-muted)',
        }}>
          <NexusIcon name="Mail" size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
          <div style={{ fontSize: 14 }}>No email providers configured.</div>
          {!readOnly && (
            <div style={{ marginTop: 12 }}>
              <Btn variant="secondary" icon="Plus" onClick={openAdd}>Add Provider</Btn>
            </div>
          )}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {providers.map(p => (
          <ProviderCard
            key={p.id}
            provider={p}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onTest={handleTest}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            testingId={testingId}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  };

  const getSaveLabel = () => {
    if (!modal) return '';
    if (modal.saving) return 'Saving…';
    return modal.mode === 'add' ? 'Add Provider' : 'Save Changes';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginTop: 2 }}>
          {summaryText}
        </div>
        {!readOnly && providers.length > 0 && (
          <Btn variant="secondary" icon="Plus" onClick={openAdd}>
            Add Provider
          </Btn>
        )}
      </div>

      {renderList()}

      {/* Add / Edit modal */}
      {modal && (
        <Modal
          title={modal.mode === 'add' ? 'Add Email Provider' : 'Edit Email Provider'}
          onClose={() => !modal.saving && setModal(null)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setModal(null)} disabled={modal.saving}>Cancel</Btn>
              <Btn variant="primary" onClick={handleSave} disabled={modal.saving}>
                {getSaveLabel()}
              </Btn>
            </>
          }
        >
          <ProviderForm
            form={modal.form}
            onChange={form => setModal(m => ({ ...m, form }))}
            isEdit={modal.mode === 'edit'}
          />
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <DeleteConfirm
          provider={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
